import { useCallback, useMemo } from 'react'

import { CameraViewOrientation } from '@editor/interfaces/camera'
import { getClickCoords } from '@editor/services/Points'
import { RootState } from '@editor/store/app'
import { useSelector } from 'react-redux'
import { Camera, Matrix4, Plane, Raycaster, Vector2, Vector3, WebGLRenderer } from 'three'

const raycaster = new Raycaster()

const useProjectionPlane = (camera?: Camera, gl?: WebGLRenderer) => {
  // Store
  const selectedBlueprint = useSelector((state: RootState) => state.blueprint.editor.selectedBlueprint)

  /**
   * Calculate the projection matrix based on the selected blueprint's camera profile.
   */
  const projectionMatrix = useMemo(() => {
    if (!selectedBlueprint?.camera_profile?.target) return new Matrix4()

    const {
      state: {
        arcballState: {
          cameraMatrix: { elements: cameraMatrix },
        },
      },
    } = selectedBlueprint.camera_profile

    return new Matrix4().fromArray(cameraMatrix)
  }, [selectedBlueprint?.camera_profile])

  /**
   * Calculate the projection directions based on the selected blueprint's camera profile.
   * This includes the up, left, and normal vectors of the projection plane.
   */
  const projectionDirections = useMemo(() => {
    if (!selectedBlueprint?.camera_profile) return null

    const {
      target,
      positions,
      state: {
        arcballState: {
          cameraMatrix: { elements: cameraMatrix },
        },
      },
    } = selectedBlueprint.camera_profile

    const cameraPosition = new Vector3(...positions[CameraViewOrientation.FRONT])
    const cameraMatrixUp = new Vector3(...cameraMatrix.slice(4, 7))

    const targetV3 = new Vector3(...target)
    const direction = new Vector3().subVectors(targetV3, cameraPosition).normalize()
    const left = new Vector3().crossVectors(cameraMatrixUp, direction).normalize()
    const up = new Vector3().crossVectors(direction, left).normalize()

    // Create rotation matrix to align plane normal with z-axis
    const zAxis = new Vector3(0, 0, 1)
    const zMtrx = new Matrix4()

    if (!direction.equals(zAxis)) {
      const axis = direction.clone().cross(zAxis).normalize()
      const angle = Math.acos(direction.dot(zAxis))
      zMtrx.makeRotationAxis(axis, angle)
    }

    return {
      up,
      left,
      normal: direction,
      zAlignMatrix: zMtrx,
      zAlignMatrixInverse: zMtrx.clone().invert(),
    }
  }, [selectedBlueprint])

  /**
   * Calculate the projection plane based on the selected blueprint and mode.
   */
  const projectionPlane = useMemo(() => {
    if (!selectedBlueprint?.sort_key || !projectionDirections || !selectedBlueprint.camera_profile) {
      return null
    }

    return new Plane().setFromNormalAndCoplanarPoint(
      projectionDirections.normal.clone().negate(),
      new Vector3(...selectedBlueprint.camera_profile.target),
    )
  }, [selectedBlueprint, projectionDirections])

  /**
   * Calculate an offset matrix to slightly offset objects from the projection plane to avoid z-fighting.
   *
   * @param offset Distance to offset along the projection normal. Default is 0.001 units.
   */
  const calculateOffsetMatrix = useCallback(
    (offset: number = 1) => {
      if (!projectionDirections) return new Matrix4()

      const translation = projectionDirections.normal
        .clone()
        .negate()
        .multiplyScalar((offset * 2) / 1000) // Convert m to mm
      const offsetMatrix = new Matrix4().makeTranslation(translation.x, translation.y, translation.z)
      return offsetMatrix
    },
    [projectionDirections],
  )

  /**
   * Raycast to the projection plane from a screen position.
   * @param screenPos - The screen position to raycast from.
   * @param result - The Vector3 to store the intersection result
   * @returns The intersection point or null if projection plane is not set
   */
  const raycastToPlane = useCallback(
    (screenPos: { x: number; y: number }, result: Vector3) => {
      if (!projectionPlane || !camera || !gl?.domElement) return null

      const { x, y, width, height } = gl.domElement.getBoundingClientRect()
      const { x: screenX, y: screenY } = screenPos
      const { coords } = getClickCoords(
        {
          x: screenX,
          y: screenY,
        },
        {
          x,
          y,
          width,
          height,
        },
      )

      raycaster.setFromCamera(coords, camera)
      return raycaster.ray.intersectPlane(projectionPlane, result)
    },
    [camera, gl?.domElement, projectionPlane],
  )

  /**
   * Projects a 3D world position to 2D screen coordinates.
   *
   * @param pos World position to project.
   * @param camera Camera used for projection.
   * @param width Viewport width in pixels.
   * @param height Viewport height in pixels.
   * @returns Screen space coordinates in pixels.
   */
  const worldToScreen = (pos: Vector3): Vector2 | null => {
    if (!camera) return null

    const canvas = document.querySelector('#canvasBox canvas')
    if (!canvas) return null

    const { width, height } = canvas.getBoundingClientRect()
    const ndc = pos.clone().project(camera)

    return new Vector2((ndc.x * 0.5 + 0.5) * width, (ndc.y * -0.5 + 0.5) * height)
  }
  /**
   * Converts 2D screen coordinates into a 3D world position on a given plane.
   *
   * @param screenPos The position in screen space (pixels).
   * @param camera The camera used for unprojection.
   * @param width The viewport width in pixels.
   * @param height The viewport height in pixels.
   * @param plane The plane in world space to intersect with.
   * @returns The intersection point in world space, or null if no intersection exists.
   */
  const screenToWorld = (screenPos: Vector2): Vector3 | null => {
    if (!camera || !projectionPlane) return null

    const canvas = document.querySelector('#canvasBox canvas')
    if (!canvas) return null

    const { width, height } = canvas.getBoundingClientRect()
    const ndc = new Vector2((screenPos.x / width) * 2 - 1, -(screenPos.y / height) * 2 + 1)

    raycaster.setFromCamera(ndc, camera)

    const target = new Vector3()
    const hit = raycaster.ray.intersectPlane(projectionPlane, target)

    return hit ? target : null
  }

  return {
    projectionMatrix,
    projectionPlane,
    projectionDirections,
    worldToScreen,
    screenToWorld,
    raycastToPlane,
    calculateOffsetMatrix,
  }
}

export default useProjectionPlane
